import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import * as crmApi from '../utils/crmApi';
import '../crm.css';
import { crmBasePath } from '../utils/crmEnv';

const actions = [
    { id: 'create-lead', label: 'Create Lead', path: 'leads', event: 'crm:quickAddLead' },
    { id: 'create-contact', label: 'Create Contact', path: 'contacts', event: 'crm:quickAddContact' },
    { id: 'create-deal', label: 'Create Deal', path: 'deals', event: 'crm:quickAddDeal' },
    { id: 'create-activity', label: 'Create Activity', path: 'activities', event: 'crm:quickAddActivity' },
    { id: 'followups', label: 'Go to Follow-ups board', path: 'dashboard' },
    { id: 'pipeline', label: 'Go to Deals Pipeline', path: 'pipeline' },
];

function CommandPalette({ isOpen, onClose }) {
    const history = useHistory();
    const location = useLocation();
    const basePath = crmBasePath();
    const inputRef = useRef(null);
    const [query, setQuery] = useState('');
    const [loading, setLoading] = useState(false);
    const [results, setResults] = useState([]);
    const [activeIndex, setActiveIndex] = useState(0);

    useEffect(() => {
        if (isOpen) {
            setQuery('');
            setResults([]);
            setActiveIndex(0);
            setTimeout(() => inputRef.current?.focus(), 50);
        }
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) return;
        const handleKey = (event) => {
            if (event.key === 'Escape') {
                event.preventDefault();
                onClose?.();
                return;
            }
            if (event.key === 'ArrowDown') {
                event.preventDefault();
                setActiveIndex((prev) => Math.min(prev + 1, results.length - 1));
            }
            if (event.key === 'ArrowUp') {
                event.preventDefault();
                setActiveIndex((prev) => Math.max(prev - 1, 0));
            }
            if (event.key === 'Enter') {
                event.preventDefault();
                const item = results[activeIndex];
                if (item) item.onSelect?.();
            }
        };
        window.addEventListener('keydown', handleKey);
        return () => window.removeEventListener('keydown', handleKey);
    }, [isOpen, results, activeIndex, onClose]);

    useEffect(() => {
        if (!isOpen) return;
        const trimmed = query.trim();
        if (!trimmed) {
            const actionItems = actions.map((action) => ({
                id: action.id,
                label: action.label,
                category: 'Actions',
                onSelect: () => {
                    if (action.event) {
                        window.dispatchEvent(new CustomEvent(action.event));
                    } else {
                        history.push(`${basePath}/${action.path}`);
                    }
                    onClose?.();
                },
            }));
            setResults(actionItems);
            return;
        }

        let cancelled = false;
        const timeout = setTimeout(async () => {
            setLoading(true);
            try {
                const [leads, contacts, deals, properties] = await Promise.all([
                    crmApi.getLeads({ search: trimmed, pageSize: 5 }),
                    crmApi.getContacts({ search: trimmed, pageSize: 5 }),
                    crmApi.getDeals({ q: trimmed, pageSize: 5 }),
                    crmApi.getProperties({ q: trimmed, pageSize: 5 }),
                ]);

                if (cancelled) return;

                const leadItems = (leads.items || leads.data || []).map((lead) => ({
                    id: `lead-${lead.id}`,
                    label: lead.name || `${lead.firstName || ''} ${lead.lastName || ''}`.trim() || 'Lead',
                    subtitle: lead.email || lead.phone || '',
                    category: 'Leads',
                    onSelect: () => {
                        history.push(`${basePath}/leads/${lead.id}`);
                        onClose?.();
                    },
                }));
                const contactItems = (contacts.items || contacts.data || []).map((contact) => ({
                    id: `contact-${contact.id}`,
                    label: `${contact.firstName || ''} ${contact.lastName || ''}`.trim() || 'Contact',
                    subtitle: contact.email || contact.phone || '',
                    category: 'Contacts',
                    onSelect: () => {
                        history.push(`${basePath}/contacts/${contact.id}`);
                        onClose?.();
                    },
                }));
                const dealItems = (deals.items || deals.data || []).map((deal) => ({
                    id: `deal-${deal.id}`,
                    label: deal.title || 'Deal',
                    subtitle: deal.stage || '',
                    category: 'Deals',
                    onSelect: () => {
                        history.push(`${basePath}/deals/${deal.id}`);
                        onClose?.();
                    },
                }));
                const propertyItems = (properties.data || []).map((property) => ({
                    id: `property-${property.id}`,
                    label: property.title || 'Property',
                    subtitle: [property.locality, property.city].filter(Boolean).join(', '),
                    category: 'Properties',
                    onSelect: () => {
                        history.push(`${basePath}/properties`);
                        onClose?.();
                    },
                }));

                const actionItems = actions.map((action) => ({
                    id: action.id,
                    label: action.label,
                    category: 'Actions',
                    onSelect: () => {
                        if (action.event) {
                            window.dispatchEvent(new CustomEvent(action.event));
                        } else {
                            history.push(`${basePath}/${action.path}`);
                        }
                        onClose?.();
                    },
                }));

                setResults([...leadItems, ...contactItems, ...dealItems, ...propertyItems, ...actionItems]);
                setActiveIndex(0);
            } catch (err) {
                console.error('Search error:', err);
                setResults([]);
            } finally {
                if (!cancelled) setLoading(false);
            }
        }, 250);

        return () => {
            cancelled = true;
            clearTimeout(timeout);
        };
    }, [query, isOpen, history, basePath, onClose]);

    const groupedResults = useMemo(() => {
        const groups = {};
        results.forEach((item) => {
            const category = item.category || 'Other';
            groups[category] = groups[category] || [];
            groups[category].push(item);
        });
        return groups;
    }, [results]);

    if (!isOpen) return null;

    return (
        <div className="crm-command-overlay" onClick={onClose}>
            <div className="crm-command-panel" onClick={(event) => event.stopPropagation()}>
                <div className="crm-command-input">
                    <input
                        ref={inputRef}
                        type="text"
                        placeholder="Search leads, contacts, deals, properties..."
                        value={query}
                        onChange={(event) => setQuery(event.target.value)}
                    />
                    {loading && <span className="crm-command-loading">Searching...</span>}
                </div>
                <div className="crm-command-results">
                    {Object.keys(groupedResults).length === 0 && (
                        <div className="crm-command-empty">No results</div>
                    )}
                    {Object.entries(groupedResults).map(([category, items]) => (
                        <div key={category} className="crm-command-group">
                            <div className="crm-command-group-label">{category}</div>
                            {items.map((item) => {
                                const index = results.findIndex((entry) => entry.id === item.id);
                                const isActive = index === activeIndex;
                                return (
                                    <button
                                        key={item.id}
                                        className={`crm-command-item ${isActive ? 'active' : ''}`}
                                        onMouseEnter={() => setActiveIndex(index)}
                                        onClick={() => item.onSelect?.()}
                                        type="button"
                                    >
                                        <div>
                                            <div className="crm-command-label">{item.label}</div>
                                            {item.subtitle && <div className="crm-command-subtitle">{item.subtitle}</div>}
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

export default CommandPalette;
